import { ActionConfirmationHost, usePublishActionOutcome } from "./ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { get, send, type NoteRecipientList } from "./api.js";
import { useAsync } from "./useAsync.js";

/**
 * The extra addresses a student's lesson notes also go to.
 *
 * The same component on both sides — a tutor or the owner looking at a student
 * record, and a parent looking at their own child — because it is the same
 * thing being done. The server says who may add and who may remove each one;
 * this only draws what it is told, which is why nothing here re-implements the
 * rule.
 */
export function NoteRecipients({ base }: { base: string }) {
  return <ActionConfirmationHost key={base}><NoteRecipientsContent base={base} /></ActionConfirmationHost>;
}

function NoteRecipientsContent({ base }: { base: string }) {
  const publish = usePublishActionOutcome();
  const { data, error, loading, reload } = useAsync(
    () => get<NoteRecipientList>(`${base}/recipients`),
    [base],
  );

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await send("POST", `${base}/recipients`, { email });
      publish({ variant: "success", title: "Lesson-note recipient added", description: email });
      setEmail("");
      reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add that address.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setProblem(null);
    try {
      await send("DELETE", `${base}/recipients/${id}`);
      publish({ variant: "success", title: "Lesson-note recipient removed", description: data?.recipients.find((recipient) => recipient.id === id)?.email });
      reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not remove that address.");
    }
  }

  if (loading || !data) return null;
  if (error) return <div className="error small">{error}</div>;

  // Nothing to say to somebody who can neither see an addition nor make one.
  if (!data.canAdd && data.recipients.length === 0) return null;

  return (
    <div className="panel">
      <h2 className="small" style={{ margin: "0 0 4px" }}>
        Lesson notes also go to
      </h2>
      <p className="small muted" style={{ margin: "0 0 10px" }}>
        As well as the addresses the school holds. Useful for a student with their own email, or a
        second home.
      </p>

      {problem && <div className="error small">{problem}</div>}

      {data.recipients.length === 0 && <p className="small muted">Nobody else, so far.</p>}

      {data.recipients.map((recipient) => (
        <div className="row" key={recipient.id} style={{ marginBottom: 6 }}>
          <span>{recipient.email}</span>
          {recipient.addedBy && <span className="small muted">added by {recipient.addedBy}</span>}
          {recipient.canRemove && (
            <button className="quiet small" onClick={() => void remove(recipient.id)}>
              Remove
            </button>
          )}
        </div>
      ))}

      {data.canAdd && (
        <form className="row" onSubmit={add} style={{ marginTop: 10 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="another@example.com"
            autoCapitalize="none"
            spellCheck={false}
            required
            style={{ flex: 1, minWidth: 200 }}
          />
          <button type="submit" disabled={busy}>
            {busy ? "Adding…" : "Add"}
          </button>
        </form>
      )}
    </div>
  );
}
